import { Paper, Stack, Typography, IconButton, Box } from '@mui/material'
import FilePresentIcon from '@mui/icons-material/FilePresent'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

interface DocumentDetailDisplayProps {
  file?: File
  fileName?: string
  fileSize?: number
  status: string
  showDeleteButton?: boolean
  showCompleteIcon?: boolean
  onDelete?: () => void
}

function DocumentDetailDisplay({
  file,
  fileName,
  fileSize,
  status,
  showDeleteButton = false,
  showCompleteIcon = false,
  onDelete
}: DocumentDetailDisplayProps) {
  function formatBytes(bytes: number, decimals = 2) {
    if (!+bytes) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
  }

  const displayName = file?.name || fileName || 'Unknown file'
  const displaySize = file?.size || fileSize

  return (
    <Paper
      variant='outlined'
      style={{
        padding: 2,
        cursor: 'pointer',
        background:
          status === 'error'
            ? 'rgba(211, 47, 47, 0.08)'
            : 'rgba(49, 111, 245, 0.04)',
        display: 'flex',
        flexDirection: 'row',
        border: 'none',
        alignItems: 'center'
      }}>
      <FilePresentIcon
        style={{
          fontSize: 24,
          color: status === 'error' ? 'rgba(0, 0, 0, 0.3)' : '#002678',
          margin: 10
        }}
      />
      <div
        style={{
          margin: 10,
          fontSize: 16,
          color: '#212121',
          wordBreak: 'break-word',
          flex: 1
        }}>
        {displayName}
        <Stack spacing={1} direction={'row'} alignItems={'center'}>
          {displaySize !== undefined && (
            <>
              <Typography sx={{ color: '#666666', fontSize: 14 }}>
                {formatBytes(displaySize)}
              </Typography>
              <Typography sx={{ color: '#666666', fontSize: 8 }}>
                {' '}
                &bull;{' '}
              </Typography>
            </>
          )}
          <Typography sx={{ color: '#666666', fontSize: 14 }}>
            {status}
          </Typography>
        </Stack>
      </div>
      <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
        {status === 'loading' && (
          <CircularProgress size={16} sx={{ color: '#002678', mr: 1 }} />
        )}
        {showDeleteButton && onDelete && (
          <IconButton
            size='small'
            onClick={e => {
              e.stopPropagation()
              onDelete()
            }}
            sx={{ color: 'text.secondary' }}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        )}
        {showCompleteIcon && status !== 'loading' && status !== 'error' && (
          <CheckCircleIcon
            sx={{ color: 'success.main', fontSize: 24, ml: 0.5 }}
          />
        )}
      </Box>
    </Paper>
  )
}

export default DocumentDetailDisplay
